import { useState } from "react";
import { api } from "../../api";
import type { MediaRow } from "../../types";
import Icon from "../../components/Icon";

type MediaEditorProps = {
  assetId: string;
  initialMedia: MediaRow[];
};

type MediaDraft = {
  image_url: string;
  cf_id: string;
  alt_text: string;
  is_primary: number;
  role: string;
  alt_status: string;
  aspect: string;
  caption: string;
};

const BLANK_DRAFT: MediaDraft = {
  image_url: "",
  cf_id: "",
  alt_text: "",
  is_primary: 0,
  role: "",
  alt_status: "draft",
  aspect: "",
  caption: "",
};

const EYEBROW: React.CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.1em",
  color: "var(--fg-muted)",
  fontWeight: 500,
};

function rowToDraft(m: MediaRow): MediaDraft {
  return {
    image_url: m.image_url,
    cf_id: m.cf_id ?? "",
    alt_text: m.alt_text ?? "",
    is_primary: m.is_primary,
    role: m.role ?? "",
    alt_status: m.alt_status ?? "draft",
    aspect: m.aspect ?? "",
    caption: m.caption ?? "",
  };
}

function FormFields({
  draft,
  onChange,
  showUrl,
}: {
  draft: MediaDraft;
  onChange: (patch: Partial<MediaDraft>) => void;
  showUrl: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "10px 16px",
      }}
    >
      {showUrl && (
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={{ ...EYEBROW, marginBottom: 4 }}>Image URL *</div>
          <input
            className="input"
            value={draft.image_url}
            onChange={(e) => onChange({ image_url: e.target.value })}
            placeholder="https://…"
          />
        </div>
      )}
      <div>
        <div style={{ ...EYEBROW, marginBottom: 4 }}>CF ID</div>
        <input
          className="input input-mono"
          value={draft.cf_id}
          onChange={(e) => onChange({ cf_id: e.target.value })}
          placeholder="cloudflare images id"
          style={{ fontSize: 12 }}
        />
      </div>
      <div>
        <div style={{ ...EYEBROW, marginBottom: 4 }}>Alt text</div>
        <input
          className="input"
          value={draft.alt_text}
          onChange={(e) => onChange({ alt_text: e.target.value })}
        />
      </div>
      <div>
        <div style={{ ...EYEBROW, marginBottom: 4 }}>Role</div>
        <select
          className="input"
          value={draft.role}
          onChange={(e) => onChange({ role: e.target.value })}
        >
          <option value="">—</option>
          <option value="hero">hero</option>
          <option value="gallery">gallery</option>
        </select>
      </div>
      <div>
        <div style={{ ...EYEBROW, marginBottom: 4 }}>Alt status</div>
        <select
          className="input"
          value={draft.alt_status}
          onChange={(e) => onChange({ alt_status: e.target.value })}
        >
          <option value="draft">draft</option>
          <option value="aa-approved">aa-approved</option>
          <option value="rejected-unusable">rejected-unusable</option>
        </select>
      </div>
      <div>
        <div style={{ ...EYEBROW, marginBottom: 4 }}>Aspect</div>
        <input
          className="input input-mono"
          value={draft.aspect}
          onChange={(e) => onChange({ aspect: e.target.value })}
          placeholder="e.g. 4x5"
          style={{ fontSize: 12 }}
        />
      </div>
      <div>
        <div style={{ ...EYEBROW, marginBottom: 4 }}>Caption</div>
        <input
          className="input"
          value={draft.caption}
          onChange={(e) => onChange({ caption: e.target.value })}
        />
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={draft.is_primary === 1}
            onChange={(e) => onChange({ is_primary: e.target.checked ? 1 : 0 })}
          />
          <span style={{ fontSize: 13 }}>Primary image</span>
        </label>
      </div>
    </div>
  );
}

export default function MediaEditor({ assetId, initialMedia }: MediaEditorProps) {
  const [media, setMedia] = useState<MediaRow[]>(initialMedia);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<MediaDraft>(BLANK_DRAFT);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState<MediaDraft>(BLANK_DRAFT);
  const [addSaving, setAddSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit(m: MediaRow) {
    setEditingId(m.id);
    setEditDraft(rowToDraft(m));
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft(BLANK_DRAFT);
  }

  async function saveEdit(mediaId: string) {
    setSavingId(mediaId);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        cf_id: editDraft.cf_id || null,
        alt_text: editDraft.alt_text || null,
        is_primary: editDraft.is_primary,
        role: editDraft.role || null,
        alt_status: editDraft.alt_status || null,
        aspect: editDraft.aspect || null,
        caption: editDraft.caption || null,
      };
      await api.updateMedia(assetId, mediaId, body);
      setMedia((prev) =>
        prev.map((m) =>
          m.id === mediaId
            ? {
                ...m,
                cf_id: editDraft.cf_id || null,
                alt_text: editDraft.alt_text || null,
                is_primary: editDraft.is_primary,
                role: editDraft.role || null,
                alt_status: editDraft.alt_status || null,
                aspect: editDraft.aspect || null,
                caption: editDraft.caption || null,
              }
            : m
        )
      );
      setEditingId(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingId(null);
    }
  }

  async function deleteMedia(mediaId: string) {
    setDeletingId(mediaId);
    setError(null);
    try {
      await api.deleteMedia(assetId, mediaId);
      setMedia((prev) => prev.filter((m) => m.id !== mediaId));
      if (editingId === mediaId) setEditingId(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  }

  async function addMedia() {
    if (!addDraft.image_url.trim()) return;
    setAddSaving(true);
    setError(null);
    try {
      const res = await api.createMedia(assetId, {
        image_url: addDraft.image_url.trim(),
        cf_id: addDraft.cf_id || undefined,
        alt_text: addDraft.alt_text || undefined,
        is_primary: addDraft.is_primary,
        role: addDraft.role || undefined,
        alt_status: addDraft.alt_status || undefined,
        aspect: addDraft.aspect || undefined,
        caption: addDraft.caption || undefined,
        sort_order: media.length,
      });
      const newRow: MediaRow = {
        id: res.id,
        asset_id: res.asset_id,
        image_url: addDraft.image_url.trim(),
        alt_text: addDraft.alt_text || null,
        sort_order: media.length,
        is_primary: addDraft.is_primary,
        cf_id: addDraft.cf_id || null,
        role: addDraft.role || null,
        alt_status: addDraft.alt_status || null,
        bg_classification: null,
        aspect: addDraft.aspect || null,
        filename: null,
        media_type: null,
        description: null,
        tier: null,
        source: null,
        src: null,
        caption: addDraft.caption || null,
      };
      setMedia((prev) => [...prev, newRow]);
      setAddDraft(BLANK_DRAFT);
      setAdding(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAddSaving(false);
    }
  }

  return (
    <div className="field-group" style={{ marginBottom: 16 }}>
      <div className="field-group-head">
        <span style={EYEBROW}>Media</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-muted)" }}>
            {media.length} file{media.length !== 1 ? "s" : ""}
          </span>
          <button
            className="btn btn-quiet btn-sm"
            onClick={() => { setAdding(true); setError(null); setEditingId(null); }}
            style={{ fontSize: 12 }}
          >
            <Icon name="plus" size={12} /> Add
          </button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 8, fontSize: 12, fontFamily: "var(--font-mono)", color: "#e58a6d" }}>
          {error}
        </div>
      )}

      {/* Thumbnail grid */}
      {media.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {media.map((m) => (
            <div key={m.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div
                style={{
                  position: "relative",
                  width: 80,
                  height: 80,
                  borderRadius: "var(--r-sm)",
                  overflow: "hidden",
                  border:
                    editingId === m.id
                      ? "2px solid var(--accent)"
                      : m.is_primary
                      ? "2px solid var(--warm-bright)"
                      : "1px solid var(--rule-strong)",
                  background: "var(--surface-sunk)",
                  flexShrink: 0,
                }}
              >
                <img
                  src={m.image_url}
                  alt={m.alt_text ?? ""}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  loading="lazy"
                />
                {m.is_primary === 1 && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 2,
                      left: 2,
                      background: "var(--warm-bright)",
                      borderRadius: 3,
                      padding: "1px 4px",
                      fontSize: 9,
                      fontFamily: "var(--font-mono)",
                      color: "#16212e",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    primary
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 3 }}>
                <button
                  className="btn btn-quiet btn-sm"
                  onClick={() => editingId === m.id ? cancelEdit() : startEdit(m)}
                  style={{ fontSize: 11, flex: 1, justifyContent: "center" }}
                >
                  {editingId === m.id ? "Close" : <Icon name="pencil" size={11} />}
                </button>
                <button
                  className="btn btn-quiet btn-sm"
                  onClick={() => void deleteMedia(m.id)}
                  disabled={deletingId === m.id}
                  style={{ fontSize: 11, color: "var(--fg-muted)" }}
                >
                  {deletingId === m.id ? "…" : <Icon name="trash" size={11} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit form */}
      {editingId && (
        <div
          style={{
            background: "var(--surface-raised)",
            border: "1px solid var(--rule)",
            borderRadius: "var(--r-sm)",
            padding: "14px",
            marginBottom: 12,
          }}
        >
          <div
            style={{
              ...EYEBROW,
              marginBottom: 10,
              color: "var(--accent)",
            }}
          >
            Editing media record
          </div>
          <FormFields
            draft={editDraft}
            onChange={(patch) => setEditDraft((d) => ({ ...d, ...patch }))}
            showUrl={false}
          />
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => void saveEdit(editingId)}
              disabled={savingId === editingId}
            >
              {savingId === editingId ? "Saving…" : "Save"}
            </button>
            <button className="btn btn-quiet btn-sm" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Add form */}
      {adding && (
        <div
          style={{
            background: "var(--surface-raised)",
            border: "1px solid var(--rule)",
            borderRadius: "var(--r-sm)",
            padding: "14px",
            marginBottom: 12,
          }}
        >
          <div style={{ ...EYEBROW, marginBottom: 10 }}>Add media record</div>
          <FormFields
            draft={addDraft}
            onChange={(patch) => setAddDraft((d) => ({ ...d, ...patch }))}
            showUrl={true}
          />
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => void addMedia()}
              disabled={addSaving || !addDraft.image_url.trim()}
            >
              {addSaving ? "Adding…" : "Add"}
            </button>
            <button
              className="btn btn-quiet btn-sm"
              onClick={() => { setAdding(false); setAddDraft(BLANK_DRAFT); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {media.length === 0 && !adding && (
        <div style={{ fontSize: 13, color: "var(--fg-subtle)", fontStyle: "italic" }}>
          No media yet. Use Add to attach a CF Images reference.
        </div>
      )}
    </div>
  );
}
